import { useEffect, useState } from 'react';

import {
  MS_PER_DAY,
  SUPPRESSIONS_EXPORT_MAX_DEVICES,
  SUPPRESSIONS_EXPORT_MAX_RANGE_DAYS,
} from '../../constants/suppressions.js';
import { useIntl } from '../../hooks';
import { useSuppressionsExport } from '../../hooks/useSuppressionsExport.js';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui';

type ExportWindow = {
  deviceIds: string[];
  from?: string;
  to?: string;
};

export const getExportBlockReason = (
  { deviceIds, from, to }: ExportWindow,
  now: number,
):
  | 'needsDevices'
  | 'needsFrom'
  | 'tooManyDevices'
  | 'fromInFuture'
  | 'toBeforeFrom'
  | 'rangeTooLong'
  | null => {
  if (deviceIds.length === 0) return 'needsDevices';
  if (!from) return 'needsFrom';
  if (deviceIds.length > SUPPRESSIONS_EXPORT_MAX_DEVICES) return 'tooManyDevices';

  const fromMs = new Date(from).getTime();
  if (fromMs >= now) return 'fromInFuture';
  const end = Math.min(to ? new Date(to).getTime() : now, now);
  if (end <= fromMs) return 'toBeforeFrom';
  if (end - fromMs > SUPPRESSIONS_EXPORT_MAX_RANGE_DAYS * MS_PER_DAY) return 'rangeTooLong';
  return null;
};

export function SuppressionsReportCard({ deviceIds, from, to }: ExportWindow) {
  const { formatMessage } = useIntl();
  const { mutate, isPending } = useSuppressionsExport();
  // Date.now() is impure in render, so the open-ended window is judged against a timestamp
  // sampled when the window changes; the click handler re-checks against the live clock.
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    setNow(Date.now());
  }, [deviceIds, from, to]);
  // A window starting in the future unblocks by itself once the clock passes its start.
  const waitsForFrom = from !== undefined && new Date(from).getTime() >= now;
  useEffect(() => {
    if (!waitsForFrom || !from) return;
    const timer = setTimeout(() => setNow(Date.now()), new Date(from).getTime() - Date.now() + 1);
    return () => clearTimeout(timer);
  }, [waitsForFrom, from]);
  const reason = getExportBlockReason({ deviceIds, from, to }, now);

  return (
    <Card data-testid="suppressions-report-card">
      <CardHeader>
        <CardTitle className="text-lg">
          {formatMessage({ id: 'analytics.suppressionsReport.title' })}
        </CardTitle>
        <CardDescription>
          {formatMessage({ id: 'analytics.suppressionsReport.description' })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-start gap-2">
        <Button
          data-testid="suppressions-report-download"
          disabled={reason !== null || isPending}
          aria-describedby={reason ? 'suppressions-report-hint' : undefined}
          onClick={() => {
            const clickedAt = Date.now();
            setNow(clickedAt);
            if (from && getExportBlockReason({ deviceIds, from, to }, clickedAt) === null) {
              mutate({ deviceIds, from, to });
            }
          }}
        >
          {formatMessage({
            id: isPending
              ? 'analytics.suppressionsReport.downloading'
              : 'analytics.suppressionsReport.download',
          })}
        </Button>
        {reason && (
          <p
            id="suppressions-report-hint"
            data-testid="suppressions-report-hint"
            className="text-sm text-muted-foreground"
          >
            {formatMessage(
              { id: `analytics.suppressionsReport.${reason}` },
              {
                max:
                  reason === 'tooManyDevices'
                    ? SUPPRESSIONS_EXPORT_MAX_DEVICES
                    : SUPPRESSIONS_EXPORT_MAX_RANGE_DAYS,
              },
            )}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
